import { ConsentState, CONSENT_VERSION } from './types'

const CONSENT_COOKIE = 'cookie_consent'
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60

export function getConsentCookie(): ConsentState | null {
  if (typeof document === 'undefined') return null

  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`))

    if (!match) return null

    return JSON.parse(decodeURIComponent(match[1]))
  } catch {
    return null
  }
}

export function setConsentCookie(state: ConsentState): void {
  if (typeof document === 'undefined') return

  const value = encodeURIComponent(JSON.stringify(state))
  const isSecure = window.location.protocol === 'https:'
  const parts = [
    `${CONSENT_COOKIE}=${value}`,
    'path=/',
    `max-age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ]

  if (isSecure) parts.push('Secure')

  document.cookie = parts.join('; ')
}

export function updateGtagConsent(analytics: boolean, marketing: boolean): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return

  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  })
}

export function injectMetaPixel(): void {
  if (typeof window === 'undefined') return
  if (typeof window.fbq === 'function') return // Already loaded

  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID

  if (!pixelId) return

  // Initialize fbq stub before script loads
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const w = window as any
  const n = (w.fbq = function fbqStub(...args: any[]) {
    if (n.callMethod) {
      n.callMethod(...args)
    } else {
      n.queue.push(args)
    }
  }) as any

  w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = [] as any[]
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const script = document.createElement('script')

  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  w.fbq('init', pixelId)
  w.fbq('track', 'PageView')
}

export function removeMetaPixelCookies(): void {
  if (typeof document === 'undefined') return

  const cookiesToRemove = ['_fbp', '_fbc']

  for (const name of cookiesToRemove) {
    document.cookie = `${name}=; path=/; max-age=0`
    document.cookie = `${name}=; path=/; max-age=0; domain=.${window.location.hostname}`
  }
}

export function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false

  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
}

export function buildConsentState(
  analytics: boolean,
  marketing: boolean,
): ConsentState {
  return {
    categories: {
      essential: true,
      analytics,
      marketing,
    },
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  }
}
